import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { Button, Spinner } from 'flowbite-react';
import { useLazyQuery } from '@apollo/client';

import PencilSquareOutline from '@/components/commons/icons/PencilSquareOutline';
import { type Brief, userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import useProfileImage from '@/hooks/useProfileImage';
import { FETCH_USER } from '@/components/with_auth/Actions';
import ProfileImage from '@/components/commons/ProfileImage';
import WarningCard from '@/components/commons/warning_card';
import FormError from '@/components/forms/FormError';

const UPLOAD_URL = routes.PHOTOS_UPLOAD();

function FileUpload() {
    const fileInputRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');

    const [fetchUser] = useLazyQuery(FETCH_USER, {
        // tell apollo to always fetch from backend
        fetchPolicy: 'network-only',
    });

    const [user, setUser] = useRecoilState(userAtom);
    const { isGravatar } = useProfileImage(user as Partial<Brief>);

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        // make sure we only upload 1 file if we have one
        if (file) {
            // search for case insensitive .jpg .jpeg or .png in the file name
            const regEx = /\.(jpe?g|png)$/i;
            if (file.name.search(regEx) === -1) {
                setError('Expected .jpg or .png file format');
                return;
            }
            const data = new FormData();
            data.append(file.name, file);

            setIsLoading(true);
            try {
                await axios.post(UPLOAD_URL, data, {
                    withCredentials: true,
                });
                // refetch the user to get the profile image we uploaded
                const response = await fetchUser();
                if (formError !== '') setFormError('');
                await setUser(response.data.user);
            } catch (_err) {
                setFormError('An unexpected error occurred.');
            }
            setIsLoading(false);
        }
    };

    return (
        <div>
            {isLoading
                ? (
                    <div className="flex items-center justify-center mb-5">
                        <Spinner size="xl" />
                    </div>
                )
                : (
                    <ProfileImage brief={user as Partial<Brief>} externalStyles="mx-auto relative mb-5">
                        {
                            formError !== '' && (
                                <div className="w-full my-[20px]">
                                    <WarningCard message={formError} />
                                </div>
                            )
                        }
                        {!isGravatar
                            && (
                                <Button
                                    color="dark"
                                    onClick={() => {
                                        handleButtonClick();
                                    }}
                                    type="button"
                                    className="absolute bottom-0 right-0 rounded-full h-[30px] w-[30px] text-cirrusWhite bg-cleanSlate enabled:hover:bg-black"
                                >
                                    <PencilSquareOutline className="h-4 w-4 mr-0 text-white" />
                                </Button>
                            )}

                    </ProfileImage>
                )}
            <input
                id="file-upload"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".jpg, .jpeg, .png"
                className="hidden"
            />
            <FormError error={error} />
        </div>
    );
}

export default FileUpload;
